import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { Close, CloudUpload, Delete, CheckCircle } from '@mui/icons-material';

interface InvoiceUploadProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
  certificate?: any;
}

const InvoiceUpload: React.FC<InvoiceUploadProps> = ({ 
  open, 
  onClose, 
  onUpload,
  certificate 
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
      setUploadSuccess(false);
    }
  };

  const handleUpload = async () => {
    if (selectedFile) {
      setIsUploading(true);
      try {
        // Simulate upload process
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        onUpload(selectedFile);
        setUploadSuccess(true);
        setSnackbarMessage('Invoice uploaded successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        
        // Close dialog after successful upload
        setTimeout(() => {
          setSelectedFile(null);
          setIsUploading(false);
          onClose();
        }, 1500);
      } catch (error) {
        setSnackbarMessage('Error uploading invoice. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setIsUploading(false);
      }
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setUploadSuccess(false);
    onClose();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
        <DialogTitle sx={{ position: 'relative', pr: 5 }}>
          Upload Invoice
          <IconButton
            onClick={handleCancel}
            size="small"
            sx={{ color: 'inherit', position: 'absolute', right: 8, top: 8 }}
            disabled={isUploading}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Upload File
              <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #ccc',
                borderRadius: 2,
                px: 2,
                py: 1,
                background: '#fff',
                minHeight: 56,
              }}
            >
              <input
                id="invoice-file-upload"
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                disabled={isUploading}
              />
              <label htmlFor="invoice-file-upload">
                <Button
                  variant="contained"
                  component="span"
                  sx={{
                    background: '#000',
                    color: '#fff',
                    textTransform: 'none',
                    mr: 2,
                    minWidth: 120,
                    '&:hover': { background: '#333' }
                  }}
                  disabled={isUploading}
                >
                  Choose File
                </Button>
              </label>
              {selectedFile ? (
                <>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ color: '#222', fontSize: 16 }}>
                      {selectedFile.name}
                    </Typography>
                    {uploadSuccess && (
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <CheckCircle fontSize="small" sx={{ color: 'green' }} />
                        <Typography variant="body2" color="green">
                          File uploaded successfully
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <IconButton
                    onClick={() => {
                      setSelectedFile(null);
                      setUploadSuccess(false);
                    }}
                    sx={{ color: '#E60012' }}
                    title="Remove file"
                    disabled={isUploading}
                  >
                    <Delete />
                  </IconButton>
                </>
              ) : (
                <>
                  <Typography sx={{ flex: 1, color: '#888', fontSize: 16 }}>
                    No file chosen
                  </Typography>
                  <IconButton disabled>
                    <CloudUpload sx={{ color: '#888' }} />
                  </IconButton>
                </>
              )}
            </Box>
            {/* {certificate && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                For: {certificate.vin} - {certificate.requestId}
              </Typography>
            )} */}
          </Box>
          {isUploading && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={isUploading}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || isUploading}
            sx={{
              backgroundColor: '#E60012',
              '&:hover': { backgroundColor: '#cc0010' },
            }}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity={snackbarSeverity} onClose={handleSnackbarClose} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InvoiceUpload;